/**
 * Telegram direct — apelăm api.telegram.org din browser.
 *
 * Tokenul vine din VITE_B_TOKEN (citit la build time din .env sau Cloudflare env vars).
 * Chat info vine din VITE_TELEGRAM_CHAT_ID / VITE_TELEGRAM_THREAD_ID.
 *
 * Notă: tokenul va fi vizibil în DevTools. E o limitare asumată — pentru proxy
 * securizat ar trebui Pages Function sau Worker.
 */

const BOT_TOKEN = (import.meta.env.VITE_B_TOKEN as string | undefined)?.trim();
const CHAT_ID = (import.meta.env.VITE_TELEGRAM_CHAT_ID as string | undefined)?.trim();
const THREAD_ID = (import.meta.env.VITE_TELEGRAM_THREAD_ID as string | undefined)?.trim();

export interface TelegramPhotoResult {
  file_id: string;
  message_id: number;
  channel: string;
}

export async function sendPhotoDirectToTelegram(
  file: Blob,
  caption: string
): Promise<TelegramPhotoResult> {
  // eslint-disable-next-line no-console
  console.log('[Telegram] sending photo', { size: file.size, type: file.type, caption: caption.slice(0, 80) });

  if (!BOT_TOKEN) throw new Error('VITE_B_TOKEN lipseste din .env / Cloudflare env vars');
  if (!CHAT_ID) throw new Error('VITE_TELEGRAM_CHAT_ID lipseste');
  if (!THREAD_ID) throw new Error('VITE_TELEGRAM_THREAD_ID lipseste');

  const fd = new FormData();
  fd.append('chat_id', CHAT_ID);
  fd.append('message_thread_id', THREAD_ID);
  fd.append('photo', file, 'photo.jpg');
  fd.append('caption', caption.slice(0, 1024));
  fd.append('parse_mode', 'HTML');

  const url = `https://api.telegram.org/bot${BOT_TOKEN}/sendPhoto`;
  // eslint-disable-next-line no-console
  console.log('[Telegram] POST', url.replace(BOT_TOKEN, '***TOKEN***'));

  const resp = await fetch(url, { method: 'POST', body: fd });
  const text = await resp.text();
  // eslint-disable-next-line no-console
  console.log('[Telegram] response status', resp.status, 'body:', text.slice(0, 500));

  let json: any;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Telegram: răspuns non-JSON (${resp.status}): ${text.slice(0, 200)}`);
  }

  if (!resp.ok || !json.ok) {
    throw new Error(`Telegram API error: ${json.description ?? text.slice(0, 200)}`);
  }

  // Telegram returnează mai multe dimensiuni; luăm cea mai mare (ultima)
  const photos: any[] = json.result.photo ?? [];
  const largest = photos[photos.length - 1];

  return {
    file_id: largest.file_id,
    message_id: json.result.message_id,
    channel: json.result.chat.username ?? String(json.result.chat.id),
  };
}

/**
 * Returnează URL-ul public al unei poze de la Telegram.
 * Telegram file URLs expiră în ~1h, deci nu le cache-uim pe termen lung.
 */
export function telegramFilePublicUrl(fileId: string): string {
  if (!BOT_TOKEN) return '';
  return `https://api.telegram.org/file/bot${BOT_TOKEN}/${fileId}`;
}

/**
 * Helper: obține file_path pentru un file_id (apelăm getFile server-side
 * printr-un proxy Pages Function — fără el, getFile din browser va fi blocat
 * de CORS. Alternativ: folosește direct telegramFilePublicUrl cu presupunerea
 * că file_id e deja file_path, ceea ce NU e corect).
 *
 * Până implementăm proxy, thumbnail-urile pot fi încărcate direct de la
 * api.telegram.org dacă setezi Access-Control-Allow-Origin — dar Telegram
 * nu trimite CORS headers, deci de obicei eșuează din browser.
 *
 * Soluție temporară: returnează empty string pentru a nu bloca UI-ul.
 */
export function getTelegramFilePath(_fileId: string): Promise<string | null> {
  // TODO: call /api/telegram-file/:file_id (Pages Function proxy)
  return Promise.resolve(null);
}
